import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { PageHeader } from "../../navigation/page-header";
import { Footer } from "../../navigation/footer/Footer";
import { fontFamily } from "../../shared/styles/theme";
import {
  black,
  gray,
  primaryColor,
  secondaryBlueColor,
  white,
} from "../../shared/styles/color";
import { desktopDevice, gap, tabletDevice } from "../../shared/styles/sizes";
import { ErrorMessage } from "../../shared/generic/headers";
import { useForm } from "react-hook-form";
import {
  getProjectByIdService,
  storeProjectService,
} from "../../../services/project/project-service";
import { useDispatch } from "react-redux";
import { logout } from "../../../redux/reducer/authReducer";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "../../../utils/swalCompat.js";
import LoadingScreen from "../../shared/loading/LoadingScreen";
import SkillsDropdown from "../../shared/inputs/SkillsDropdown";
import {
  normalizePaymentCondition,
  PAYMENT_CONDITION_OPTIONS,
} from "../../../utils/projectPaymentConditions";
import {
  FiCalendar,
  FiCheck,
  FiClock,
  FiCreditCard,
  FiEdit3,
  FiFileText,
  FiInfo,
  FiMessageCircle,
  FiPaperclip,
  FiShield,
  FiTarget,
  FiUploadCloud,
  FiUsers,
} from "react-icons/fi";

const STEPS = [
  { num: 1, title: "Project Details", sub: "Tell us about your project" },
  { num: 2, title: "Skills & Category", sub: "Choose the best fit" },
  { num: 3, title: "Budget & Matching", sub: "Rates, location & experience" },
  { num: 4, title: "Review & Publish", sub: "Finalize and post" },
];

const DEADLINE_OPTIONS = [
  "ASAP / Immediate",
  "1 Week",
  "2 Weeks",
  "1 Month",
  "3 Months",
  "6 Months",
  "9 Months",
  "1 Year",
  "Flexible",
];

const PROJECT_EXPERIENCE_LEVEL_OPTIONS = [
  { value: "", label: "Select experience level" },
  { value: "Entry Level", label: "Entry Level" },
  { value: "Junior Level", label: "Junior Level" },
  { value: "Mid level", label: "Mid Level" },
  { value: "Senior Level", label: "Senior Level" },
  { value: "Lead", label: "Lead" },
  { value: "Expert", label: "Expert" },
];

const PROJECT_YEARS_OPTIONS = [
  { value: "", label: "Any / not required" },
  ...Array.from({ length: 20 }, (_, i) => {
    const n = i + 1;
    return {
      value: String(n),
      label: n === 20 ? "20+ years" : `${n}+ years`,
    };
  }),
];

const WORK_SETUP_OPTIONS = [
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "on-site", label: "On-site" },
];

const PROJECT_LANGUAGE_OPTIONS = [
  "",
  "English",
  "Arabic",
  "Hindi",
  "Urdu",
  "French",
  "Spanish",
  "German",
  "Chinese",
];

const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.png,.jpg,.jpeg";
const MAX_DESC = 2000;
const MAX_ADDITIONAL = 1000;

/* Mockup-aligned palette */
const PAGE_BG = "#F9FAFB";
const CARD_BORDER = "#E5E7EB";
const HEADING = "#111827";
const BODY = "#6B7280";
const MUTED = "#9CA3AF";
const INPUT_BORDER = "#E5E7EB";
const TIP_BG = "#FFFBEB";
const TIP_BORDER = "#FDE68A";
const SAFE_BG = "#EFF6FF";

function HeroIllustration() {
  return (
    <HeroArt aria-hidden="true">
      <svg viewBox="0 0 280 200" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="40" y="30" width="200" height="130" rx="12" fill="#EFF7FA" stroke="#118AB2" strokeWidth="1.5" />
        <rect x="56" y="50" width="80" height="8" rx="4" fill="#118AB2" opacity="0.3" />
        <rect x="56" y="66" width="120" height="6" rx="3" fill="#99AEBB" opacity="0.4" />
        <rect x="56" y="80" width="100" height="6" rx="3" fill="#99AEBB" opacity="0.3" />
        <rect x="56" y="110" width="24" height="40" rx="4" fill="#118AB2" opacity="0.25" />
        <rect x="88" y="95" width="24" height="55" rx="4" fill="#118AB2" opacity="0.4" />
        <rect x="120" y="105" width="24" height="45" rx="4" fill="#118AB2" opacity="0.3" />
        <rect x="152" y="88" width="24" height="62" rx="4" fill="#118AB2" opacity="0.5" />
        <path d="M210 60 L250 40 L230 80 Z" fill="#118AB2" opacity="0.6" />
        <path d="M230 80 Q215 95 200 85" stroke="#118AB2" strokeWidth="1.5" fill="none" opacity="0.4" />
        <circle cx="220" cy="150" r="18" fill="#118AB2" opacity="0.15" />
        <circle cx="220" cy="150" r="8" fill="#118AB2" opacity="0.35" />
      </svg>
    </HeroArt>
  );
}

export const AddProject = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const projectId = location.state?.projectId;
  const rehireFreelancerName = location.state?.rehireFreelancerName;
  const loggedInUser = JSON.parse(localStorage.getItem("userData"));
  const role = loggedInUser?.user?.user_type;
  const [loading, setLoading] = useState(Boolean(projectId));
  const [submitting, setSubmitting] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [skills, setSkills] = useState([]);
  const [skillsHasError, setSkillsHasError] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      project_name: "",
      project_description: "",
      deadline: "",
      payment_conditions: "",
      additional_info: "",
      salary: "",
      timeline: "",
      experience_level: "",
      years_of_experience: "",
      work_setup: "remote",
      location: "",
      languages: "",
    },
  });

  const description = watch("project_description") || "";
  const additionalInfo = watch("additional_info") || "";

  useEffect(() => {
    if (role && role !== "business") {
      Swal.fire({
        title: "You are trying to access a restricted page",
        text: "Please, login again.",
        icon: "warning",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) dispatch(logout());
      });
    }
  }, [dispatch, role]);

  useEffect(() => {
    async function getProject() {
      setLoading(true);
      const project = await getProjectByIdService(projectId);
      if (project?.status === 401) {
        Swal.fire({
          title: "Session Expired!",
          text: "Please, login again.",
          icon: "warning",
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then((result) => {
          if (result.isConfirmed) dispatch(logout());
        });
      } else {
        const row = project?.data.result?.data[0];
        reset({
          ...row,
          payment_conditions: normalizePaymentCondition(row?.payment_conditions),
          additional_info: row?.additional_info || "",
          salary: row?.salary || "",
          timeline: row?.timeline || "",
          experience_level: row?.experience_level || "",
          years_of_experience: row?.years_of_experience || "",
          work_setup: row?.work_setup || "remote",
          location: row?.location || "",
          languages: row?.languages || "",
        });
        const mappedSkills = row?.requiredSkills?.map((item) => ({
          label: item.skill.skill_name,
          value: item.skill.id,
        }));
        setSkills(mappedSkills || []);
        setLoading(false);
      }
    }
    if (projectId) getProject();
  }, [dispatch, projectId, reset]);

  const addFiles = useCallback((files) => {
    const next = Array.from(files || []).filter((f) => {
      const ext = f.name.split(".").pop()?.toLowerCase();
      return ["pdf", "doc", "docx", "png", "jpg", "jpeg"].includes(ext || "");
    });
    if (!next.length) return;
    setAttachmentFiles((prev) => [...prev, ...next].slice(0, 10));
  }, []);

  const removeFileAt = (idx) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const validateStep = (step) => {
    if (step === 1) {
      const title = watch("project_name")?.trim();
      const desc = watch("project_description")?.trim();
      if (!title) return "Project title is required.";
      if (!desc) return "Project description is required.";
    }
    if (step === 2 && skills.length === 0) {
      setSkillsHasError(true);
      return "At least one skill is required.";
    }
    setSkillsHasError(false);
    return null;
  };

  const handleContinue = () => {
    const err = validateStep(currentStep);
    if (err) {
      setLoginErrorMessage(err);
      return;
    }
    setLoginErrorMessage("");
    setCurrentStep((s) => Math.min(4, s + 1));
  };

  const submitProject = async (data, saveAsDraft = false) => {
    if (!saveAsDraft) {
      const err = validateStep(1);
      if (err) {
        setLoginErrorMessage(err);
        setCurrentStep(1);
        return;
      }
      if (skills.length === 0) {
        setSkillsHasError(true);
        setLoginErrorMessage("At least one skill is required.");
        setCurrentStep(2);
        return;
      }
    }

    setSkillsHasError(false);
    setLoginErrorMessage("");
    setSubmitting(true);

    const payload = {
      ...data,
      skills_required: skills,
      save_as_draft: saveAsDraft,
      attachmentFiles,
    };
    if (projectId) payload.id = projectId;

    const response = await storeProjectService(payload);
    const body = response?.data;
    const isHtmlResponse =
      typeof body === "string" &&
      (body.includes("<!doctype html") || body.includes("<html"));
    const result = response?.data?.result;
    const looksLikeApiSuccess =
      response?.status === 200 &&
      !isHtmlResponse &&
      result != null &&
      (result.id != null || result?.dataValues?.id != null);

    if (looksLikeApiSuccess) {
      Swal.fire({
        title: saveAsDraft ? "Draft saved!" : "Successful!",
        text: saveAsDraft
          ? "Your project draft has been saved."
          : `Project successfully ${projectId ? "updated" : "posted"}.`,
        icon: "success",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) navigate("/projects");
      });
    } else {
      const msg =
        (isHtmlResponse
          ? "Your request is hitting the website (HTML) instead of the API."
          : response?.data?.message) ||
        response?.error ||
        "Project was not saved. Please try again.";
      setLoginErrorMessage(msg);
      if (response?.data?.message === "No token provided") dispatch(logout());
    }
    setSubmitting(false);
  };

  const onSaveDraft = () => {
    handleSubmit((data) => submitProject(data, true))();
  };

  const onPublish = () => {
    handleSubmit((data) => submitProject(data, false))();
  };

  const formValues = watch();

  if (loading) {
    return (
      <PageShell>
        <PageHeader variant="homepage" />
        <LoadingScreen />
        <Footer />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader variant="homepage" />

      <MainContent>
        <HeroRow>
          <HeroText>
            <HeroTitle>{projectId ? "Update Project" : "Post a Project"}</HeroTitle>
            <HeroSubtitle>
              Tell us what you need done and get matched with the right freelancers.
              It only takes a few minutes.
            </HeroSubtitle>
          </HeroText>
          <HeroIllustration />
        </HeroRow>

        {rehireFreelancerName ? (
          <RehireBanner>
            <strong>Hire again:</strong> {rehireFreelancerName} has been invited to this project.
            Complete the project details below, then they can submit their proposal.
          </RehireBanner>
        ) : null}

        <StepperRow>
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.num}>
              <StepUnit>
                <StepCircle $active={currentStep === step.num}>
                  {step.num}
                </StepCircle>
                <StepLabels>
                  <StepTitle $active={currentStep === step.num}>{step.title}</StepTitle>
                  <StepSub>{step.sub}</StepSub>
                </StepLabels>
              </StepUnit>
              {idx < STEPS.length - 1 && <StepConnector aria-hidden="true" />}
            </React.Fragment>
          ))}
        </StepperRow>

        {loginErrorMessage && <FormError>{loginErrorMessage}</FormError>}

        <ContentGrid>
          <FormColumn>
            {currentStep === 1 && (
              <>
                <FormCard>
                  <CardHeader>
                    <CardIconBox><FiEdit3 size={18} /></CardIconBox>
                    <CardHeaderText>
                      <CardTitle>Project Title</CardTitle>
                      <CardSub>Choose a clear, specific title for your project.</CardSub>
                    </CardHeaderText>
                  </CardHeader>
                  <FieldInput
                    placeholder="e.g. Build a responsive React website"
                    {...register("project_name", { required: "Project title is required" })}
                  />
                  {errors.project_name && <FieldError>{errors.project_name.message}</FieldError>}
                </FormCard>

                <FormCard>
                  <CardHeader>
                    <CardIconBox><FiFileText size={18} /></CardIconBox>
                    <CardHeaderText>
                      <CardTitle>Project Description</CardTitle>
                      <CardSub>Provide more details about your project, goals, and expectations.</CardSub>
                    </CardHeaderText>
                  </CardHeader>
                  <FieldTextarea
                    placeholder="Describe your project in detail... What do you need? What are the key requirements?"
                    maxLength={MAX_DESC}
                    {...register("project_description", {
                      required: "Project description is required",
                      maxLength: { value: MAX_DESC, message: `Max ${MAX_DESC} characters` },
                    })}
                  />
                  <CharCount>{description.length} / {MAX_DESC}</CharCount>
                  <TipBox>
                    <TipIcon>💡</TipIcon>
                    <TipText>
                      <strong>Tip:</strong> Be as specific as possible. Clear details help you get better proposals.
                    </TipText>
                  </TipBox>
                  {errors.project_description && (
                    <FieldError>{errors.project_description.message}</FieldError>
                  )}
                </FormCard>

                <FormCard>
                  <CardHeader>
                    <CardIconBox><FiPaperclip size={18} /></CardIconBox>
                    <CardHeaderText>
                      <CardTitle>Attachments (Optional)</CardTitle>
                      <CardSub>
                        Add any reference files, documents, or images to help freelancers understand your project.
                      </CardSub>
                    </CardHeaderText>
                  </CardHeader>
                  <DropZone
                    $dragging={isDragging}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      addFiles(e.dataTransfer.files);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
                  >
                    <FiUploadCloud size={32} color={primaryColor} />
                    <DropTitle>
                      Drag &amp; drop files here or{" "}
                      <DropLink>click to browse</DropLink>
                    </DropTitle>
                    <DropSub>PDF, DOC, DOCX, PNG, JPG up to 10MB each</DropSub>
                    <HiddenInput
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPTED_FILE_TYPES}
                      multiple
                      onChange={(e) => addFiles(e.target.files)}
                    />
                  </DropZone>
                  {attachmentFiles.length > 0 && (
                    <FileList>
                      {attachmentFiles.map((f, i) => (
                        <FileItem key={`${f.name}-${i}`}>
                          <span>{f.name}</span>
                          <RemoveFile type="button" onClick={() => removeFileAt(i)}>Remove</RemoveFile>
                        </FileItem>
                      ))}
                    </FileList>
                  )}
                </FormCard>

                <OptionsGrid>
                  <OptionCard>
                    <CardHeader>
                      <CardIconBox small><FiCalendar size={16} /></CardIconBox>
                      <CardHeaderText>
                        <CardTitle small>Project Deadline</CardTitle>
                        <CardSub>When do you need this project completed?</CardSub>
                      </CardHeaderText>
                    </CardHeader>
                    <FieldSelect {...register("deadline")}>
                      <option value="">Select deadline</option>
                      {DEADLINE_OPTIONS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </FieldSelect>
                  </OptionCard>

                  <OptionCard>
                    <CardHeader>
                      <CardIconBox small><FiCreditCard size={16} /></CardIconBox>
                      <CardHeaderText>
                        <CardTitle small>Payment Condition</CardTitle>
                        <CardSub>How would you like to pay?</CardSub>
                      </CardHeaderText>
                    </CardHeader>
                    <FieldSelect
                      {...register("payment_conditions", { required: "Payment type is required" })}
                    >
                      <option value="">Select payment type</option>
                      {PAYMENT_CONDITION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </FieldSelect>
                  </OptionCard>
                </OptionsGrid>

                <FormCard>
                  <CardHeader>
                    <CardIconBox><FiInfo size={18} /></CardIconBox>
                    <CardHeaderText>
                      <CardTitle>Additional Information (Optional)</CardTitle>
                      <CardSub>Add any extra details that might be useful.</CardSub>
                    </CardHeaderText>
                  </CardHeader>
                  <FieldTextarea
                    placeholder="Any additional requirements, preferences, or context..."
                    maxLength={MAX_ADDITIONAL}
                    rows={4}
                    {...register("additional_info", {
                      maxLength: { value: MAX_ADDITIONAL, message: `Max ${MAX_ADDITIONAL} characters` },
                    })}
                  />
                  <CharCount>{additionalInfo.length} / {MAX_ADDITIONAL}</CharCount>
                </FormCard>

                <ActionRow>
                  <PrimaryBtn type="button" onClick={handleContinue} disabled={submitting}>
                    Save &amp; Continue
                  </PrimaryBtn>
                  <OutlineBtn type="button" onClick={onSaveDraft} disabled={submitting}>
                    Save as Draft
                  </OutlineBtn>
                </ActionRow>
              </>
            )}

            {currentStep === 2 && (
              <>
                <FormCard>
                  <CardHeader>
                    <CardIconBox><FiTarget size={18} /></CardIconBox>
                    <CardHeaderText>
                      <CardTitle>Skills &amp; Category</CardTitle>
                      <CardSub>Select the skills required for this project.</CardSub>
                    </CardHeaderText>
                  </CardHeader>
                  <SkillsWrap>
                    <SkillsDropdown value={skills} onChange={setSkills} />
                  </SkillsWrap>
                  {skillsHasError && <FieldError>At least one skill is required.</FieldError>}
                  <SkillsHint>
                    Pick from the list, choose Other, or type a skill and press Enter.
                  </SkillsHint>
                </FormCard>
                <ActionRow>
                  <PrimaryBtn type="button" onClick={handleContinue} disabled={submitting}>
                    Save &amp; Continue
                  </PrimaryBtn>
                  <OutlineBtn type="button" onClick={() => setCurrentStep(1)} disabled={submitting}>
                    Back
                  </OutlineBtn>
                </ActionRow>
              </>
            )}

            {currentStep === 3 && (
              <>
                <FormCard>
                  <CardHeader>
                    <CardIconBox><FiCreditCard size={18} /></CardIconBox>
                    <CardHeaderText>
                      <CardTitle>Budget, timeline &amp; matching</CardTitle>
                      <CardSub>
                        These details improve freelancer match scores for your project.
                      </CardSub>
                    </CardHeaderText>
                  </CardHeader>
                  <TwoCol>
                    <FieldGroup>
                      <FieldLabel>Budget range (optional)</FieldLabel>
                      <FieldInput
                        placeholder="e.g. 500 – 2000 or 1500"
                        {...register("salary")}
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>Preferred timeline (optional)</FieldLabel>
                      <FieldInput
                        placeholder="e.g. Start ASAP / within 2 weeks"
                        {...register("timeline")}
                      />
                    </FieldGroup>
                  </TwoCol>
                  <TwoCol>
                    <FieldGroup>
                      <FieldLabel>Required experience level</FieldLabel>
                      <FieldSelect {...register("experience_level")}>
                        {PROJECT_EXPERIENCE_LEVEL_OPTIONS.map((opt) => (
                          <option key={opt.value || "exp-empty"} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </FieldSelect>
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>Minimum years of experience</FieldLabel>
                      <FieldSelect {...register("years_of_experience")}>
                        {PROJECT_YEARS_OPTIONS.map((opt) => (
                          <option key={opt.value || "yrs-empty"} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </FieldSelect>
                    </FieldGroup>
                  </TwoCol>
                  <TwoCol>
                    <FieldGroup>
                      <FieldLabel>Work setup</FieldLabel>
                      <FieldSelect {...register("work_setup")}>
                        {WORK_SETUP_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </FieldSelect>
                    </FieldGroup>
                    <FieldGroup>
                      <FieldLabel>Preferred language</FieldLabel>
                      <FieldSelect {...register("languages")}>
                        <option value="">No preference</option>
                        {PROJECT_LANGUAGE_OPTIONS.filter(Boolean).map((lang) => (
                          <option key={lang} value={lang}>
                            {lang}
                          </option>
                        ))}
                      </FieldSelect>
                    </FieldGroup>
                  </TwoCol>
                  <FieldGroup>
                    <FieldLabel>
                      Project location
                      {watch("work_setup") !== "remote" ? " (recommended)" : " (optional)"}
                    </FieldLabel>
                    <FieldInput
                      placeholder={
                        watch("work_setup") === "remote"
                          ? "e.g. Remote / Worldwide"
                          : "e.g. Dubai, United Arab Emirates"
                      }
                      {...register("location")}
                    />
                  </FieldGroup>
                </FormCard>
                <ActionRow>
                  <PrimaryBtn type="button" onClick={handleContinue} disabled={submitting}>
                    Save &amp; Continue
                  </PrimaryBtn>
                  <OutlineBtn type="button" onClick={() => setCurrentStep(2)} disabled={submitting}>
                    Back
                  </OutlineBtn>
                </ActionRow>
              </>
            )}

            {currentStep === 4 && (
              <>
                <FormCard>
                  <CardHeader>
                    <CardIconBox><FiCheck size={18} /></CardIconBox>
                    <CardHeaderText>
                      <CardTitle>Review &amp; Publish</CardTitle>
                      <CardSub>Review your project details before publishing.</CardSub>
                    </CardHeaderText>
                  </CardHeader>
                  <ReviewList>
                    <ReviewItem><ReviewLabel>Title</ReviewLabel><ReviewValue>{formValues.project_name || "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Description</ReviewLabel><ReviewValue>{formValues.project_description || "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Deadline</ReviewLabel><ReviewValue>{formValues.deadline || "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Payment</ReviewLabel><ReviewValue>{formValues.payment_conditions || "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Skills</ReviewLabel><ReviewValue>{skills.map((s) => s.label).join(", ") || "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Budget</ReviewLabel><ReviewValue>{formValues.salary || "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Timeline</ReviewLabel><ReviewValue>{formValues.timeline || "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Experience level</ReviewLabel><ReviewValue>{formValues.experience_level || "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Min. years</ReviewLabel><ReviewValue>{formValues.years_of_experience ? `${formValues.years_of_experience}+` : "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Work setup</ReviewLabel><ReviewValue>{formValues.work_setup || "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Location</ReviewLabel><ReviewValue>{formValues.location || "—"}</ReviewValue></ReviewItem>
                    <ReviewItem><ReviewLabel>Language</ReviewLabel><ReviewValue>{formValues.languages || "—"}</ReviewValue></ReviewItem>
                    {formValues.additional_info && (
                      <ReviewItem><ReviewLabel>Additional info</ReviewLabel><ReviewValue>{formValues.additional_info}</ReviewValue></ReviewItem>
                    )}
                    {attachmentFiles.length > 0 && (
                      <ReviewItem><ReviewLabel>Attachments</ReviewLabel><ReviewValue>{attachmentFiles.length} file(s)</ReviewValue></ReviewItem>
                    )}
                  </ReviewList>
                </FormCard>
                <ActionRow>
                  <PrimaryBtn type="button" onClick={onPublish} disabled={submitting}>
                    {submitting ? "Publishing…" : "Publish Project"}
                  </PrimaryBtn>
                  <OutlineBtn type="button" onClick={onSaveDraft} disabled={submitting}>
                    Save as Draft
                  </OutlineBtn>
                  <OutlineBtn type="button" onClick={() => setCurrentStep(3)} disabled={submitting}>
                    Back
                  </OutlineBtn>
                </ActionRow>
              </>
            )}
          </FormColumn>

          <Sidebar>
            <SidebarCard>
              <SidebarTitle>Tips for a Successful Project Post</SidebarTitle>
              <TipList>
                <TipRow>
                  <TipRowIcon><FiTarget size={16} /></TipRowIcon>
                  <div>
                    <TipRowTitle>Be Clear &amp; Specific</TipRowTitle>
                    <TipRowText>Detailed descriptions attract better-matched freelancers.</TipRowText>
                  </div>
                </TipRow>
                <TipRow>
                  <TipRowIcon><FiClock size={16} /></TipRowIcon>
                  <div>
                    <TipRowTitle>Set Realistic Expectations</TipRowTitle>
                    <TipRowText>Clear deadlines and budgets help freelancers plan accordingly.</TipRowText>
                  </div>
                </TipRow>
                <TipRow>
                  <TipRowIcon><FiUsers size={16} /></TipRowIcon>
                  <div>
                    <TipRowTitle>Choose the Right Skills</TipRowTitle>
                    <TipRowText>Accurate skill tags improve matching with qualified talent.</TipRowText>
                  </div>
                </TipRow>
                <TipRow>
                  <TipRowIcon><FiMessageCircle size={16} /></TipRowIcon>
                  <div>
                    <TipRowTitle>Review &amp; Respond</TipRowTitle>
                    <TipRowText>Respond promptly to proposals to secure the best talent.</TipRowText>
                  </div>
                </TipRow>
              </TipList>
            </SidebarCard>

            <SidebarCard>
              <SidebarTitle>What happens next?</SidebarTitle>
              <NextList>
                {[
                  "We'll notify relevant freelancers",
                  "Receive proposals",
                  "Review & choose",
                  "Start your project",
                ].map((text, i) => (
                  <NextItem key={text}>
                    <NextNum>{i + 1}</NextNum>
                    <span>{text}</span>
                  </NextItem>
                ))}
              </NextList>
            </SidebarCard>

            <SafeCard>
              <SafeIcon><FiShield size={22} /></SafeIcon>
              <SafeTitle>Your Project is Safe</SafeTitle>
              <SafeText>
                We protect your information and ensure secure payments through our platform.
              </SafeText>
            </SafeCard>
          </Sidebar>
        </ContentGrid>
      </MainContent>

      <Footer />
    </PageShell>
  );
};

/* ── Re-exported for other pages ── */
export const Select = styled.select`
  border: 1px solid #d1d5db;
  padding: 12px 14px;
  border-radius: 10px;
  cursor: pointer;
  width: 100%;
  font-family: ${fontFamily.font};
  font-size: 15px;
  background: ${white};
`;

export const Option = styled.option``;

export const TextArea = styled.textarea`
  border: 1px solid #d1d5db;
  min-height: 100px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  font-family: ${fontFamily.font};
  box-sizing: border-box;
`;

export const SaveButton = styled.button`
  border-radius: 100px;
  padding: 14px 40px;
  color: ${white};
  border: 1px solid ${primaryColor};
  background-color: ${primaryColor};
  font-family: ${fontFamily.font};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ButtonSection = styled.div`
  display: flex;
  gap: ${gap}px;
  flex-wrap: wrap;
`;

/* ── Page layout ── */
const PageShell = styled.div`
  min-height: 100vh;
  background: ${PAGE_BG};
  text-align: left;
`;

const MainContent = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  padding: 32px 24px 72px;
  box-sizing: border-box;

  @media (min-width: ${desktopDevice}px) {
    padding: 36px 32px 80px;
  }
`;

const RehireBanner = styled.div`
  margin-bottom: 20px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #bae6fd;
  background: #f0f9ff;
  color: #0c4a6e;
  font-size: 14px;
  line-height: 1.5;
`;

const HeroRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 28px;

  @media (max-width: ${tabletDevice - 1}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroTitle = styled.h1`
  font-family: ${fontFamily.font};
  font-size: 32px;
  font-weight: 700;
  color: ${HEADING};
  margin: 0 0 10px;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

const HeroSubtitle = styled.p`
  font-family: ${fontFamily.font};
  font-size: 15px;
  line-height: 1.6;
  color: ${BODY};
  margin: 0;
  max-width: 480px;
`;

const HeroArt = styled.div`
  flex-shrink: 0;
  width: 220px;

  svg {
    width: 100%;
    height: auto;
    display: block;
  }

  @media (max-width: ${tabletDevice - 1}px) {
    width: 180px;
    align-self: flex-end;
  }
`;

const StepperRow = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 28px;
`;

const StepUnit = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  text-align: left;
`;

const StepConnector = styled.div`
  flex: 1;
  height: 1px;
  background: ${CARD_BORDER};
  margin: 15px 16px 0;
  min-width: 24px;
  align-self: flex-start;
`;

const StepCircle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${fontFamily.font};
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;
  background: ${(p) => (p.$active ? primaryColor : white)};
  color: ${(p) => (p.$active ? white : HEADING)};
  border: 1.5px solid ${(p) => (p.$active ? primaryColor : CARD_BORDER)};
  box-sizing: border-box;
`;

const StepLabels = styled.div`
  min-width: 0;
  text-align: left;
`;

const StepTitle = styled.div`
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 700;
  color: ${HEADING};
  line-height: 1.3;
  white-space: nowrap;

  @media (min-width: ${desktopDevice}px) {
    white-space: normal;
  }
`;

const StepSub = styled.div`
  font-family: ${fontFamily.font};
  font-size: 12px;
  color: ${MUTED};
  margin-top: 2px;
  line-height: 1.35;
  white-space: nowrap;

  @media (max-width: ${tabletDevice - 1}px) {
    display: none;
  }

  @media (min-width: ${desktopDevice}px) {
    white-space: normal;
  }
`;

const FormError = styled(ErrorMessage)`
  margin-bottom: 20px;
  display: block;
`;

const ContentGrid = styled.div`
  display: grid;
  gap: 24px;
  align-items: start;

  @media (min-width: ${desktopDevice}px) {
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 28px;
  }
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const FormCard = styled.div`
  background: ${white};
  border: 1px solid ${CARD_BORDER};
  border-radius: 8px;
  padding: 20px 22px;
  text-align: left;
`;

const OptionCard = styled(FormCard)`
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  text-align: left;
  width: 100%;
`;

const CardHeaderText = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const CardIconBox = styled.div`
  width: ${(p) => (p.small ? 32 : 36)}px;
  height: ${(p) => (p.small ? 32 : 36)}px;
  border-radius: 8px;
  background: ${primaryColor};
  color: ${white};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 1px;
`;

const CardTitle = styled.h3`
  font-family: ${fontFamily.font};
  font-size: ${(p) => (p.small ? 14 : 15)}px;
  font-weight: 600;
  color: ${HEADING};
  margin: 0 0 3px;
  line-height: 1.35;
  text-align: left;
`;

const CardSub = styled.p`
  font-family: ${fontFamily.font};
  font-size: 13px;
  color: ${BODY};
  margin: 0;
  line-height: 1.45;
  text-align: left;
`;

const FieldInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${INPUT_BORDER};
  border-radius: 8px;
  padding: 11px 14px;
  font-family: ${fontFamily.font};
  font-size: 14px;
  color: ${HEADING};
  background: ${white};

  &:focus {
    outline: none;
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.1);
  }

  &::placeholder {
    color: ${MUTED};
  }
`;

const FieldTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${INPUT_BORDER};
  border-radius: 8px;
  padding: 11px 14px;
  font-family: ${fontFamily.font};
  font-size: 14px;
  color: ${HEADING};
  background: ${white};
  min-height: 130px;
  resize: vertical;
  line-height: 1.55;

  &:focus {
    outline: none;
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.1);
  }

  &::placeholder {
    color: ${MUTED};
  }
`;

const FieldSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${INPUT_BORDER};
  border-radius: 8px;
  padding: 11px 14px;
  font-family: ${fontFamily.font};
  font-size: 14px;
  color: ${HEADING};
  background: ${white};
  cursor: pointer;
  margin-top: auto;

  &:focus {
    outline: none;
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.1);
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-family: ${fontFamily.font};
  font-size: 12px;
  color: ${MUTED};
  margin-top: 6px;
`;

const TipBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: ${TIP_BG};
  border: 1px solid ${TIP_BORDER};
  border-radius: 8px;
`;

const TipIcon = styled.span`
  font-size: 15px;
  line-height: 1.3;
  flex-shrink: 0;
`;

const TipText = styled.p`
  font-family: ${fontFamily.font};
  font-size: 13px;
  color: ${BODY};
  margin: 0;
  line-height: 1.5;

  strong {
    color: ${primaryColor};
    font-weight: 600;
  }
`;

const FieldError = styled.span`
  display: block;
  color: #dc2626;
  font-family: ${fontFamily.font};
  font-size: 13px;
  margin-top: 6px;
`;

const DropZone = styled.div`
  border: 1.5px dashed ${(p) => (p.$dragging ? primaryColor : "#D1D5DB")};
  border-radius: 8px;
  padding: 32px 20px;
  text-align: center;
  cursor: pointer;
  background: ${white};
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: ${primaryColor};
    background: ${secondaryBlueColor};
  }
`;

const DropTitle = styled.p`
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 500;
  color: ${BODY};
  margin: 10px 0 4px;
`;

const DropLink = styled.span`
  color: ${primaryColor};
  font-weight: 600;
`;

const DropSub = styled.p`
  font-family: ${fontFamily.font};
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileList = styled.ul`
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FileItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
  font-family: ${fontFamily.font};
  font-size: 14px;
  color: #334155;
`;

const RemoveFile = styled.button`
  border: 0;
  background: transparent;
  color: #dc2626;
  font-family: ${fontFamily.font};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
`;

const OptionsGrid = styled.div`
  display: grid;
  gap: 16px;

  @media (min-width: ${tabletDevice}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 4px;
`;

const PrimaryBtn = styled.button`
  border: 0;
  background: ${primaryColor};
  color: ${white};
  font-family: ${fontFamily.font};
  font-weight: 600;
  font-size: 14px;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const OutlineBtn = styled.button`
  border: 1.5px solid ${primaryColor};
  background: ${white};
  color: ${primaryColor};
  font-family: ${fontFamily.font};
  font-weight: 600;
  font-size: 14px;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SkillsWrap = styled.div`
  width: 100%;
`;

const SkillsHint = styled.span`
  font-size: 13px;
  color: #64748b;
  margin-top: 8px;
  display: block;
`;

const TwoCol = styled.div`
  display: grid;
  gap: 16px;

  @media (min-width: ${tabletDevice}px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 600;
  color: #334155;
`;

const ReviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ReviewItem = styled.div`
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 12px;

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }
`;

const ReviewLabel = styled.div`
  font-family: ${fontFamily.font};
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const ReviewValue = styled.div`
  font-family: ${fontFamily.font};
  font-size: 15px;
  color: #1d1e2b;
  line-height: 1.5;
  white-space: pre-wrap;
`;

/* ── Sidebar ── */
const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SidebarCard = styled.div`
  background: ${white};
  border: 1px solid ${CARD_BORDER};
  border-radius: 8px;
  padding: 20px 22px;
  text-align: left;
`;

const SidebarTitle = styled.h4`
  font-family: ${fontFamily.font};
  font-size: 15px;
  font-weight: 600;
  color: ${HEADING};
  margin: 0 0 14px;
`;

const TipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TipRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const TipRowIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${secondaryBlueColor};
  color: ${primaryColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TipRowTitle = styled.div`
  font-family: ${fontFamily.font};
  font-size: 13px;
  font-weight: 600;
  color: ${HEADING};
  margin-bottom: 2px;
`;

const TipRowText = styled.div`
  font-family: ${fontFamily.font};
  font-size: 12px;
  color: ${BODY};
  line-height: 1.45;
`;

const NextList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const NextItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${fontFamily.font};
  font-size: 13px;
  color: ${BODY};
`;

const NextNum = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${primaryColor};
  color: ${white};
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SafeCard = styled.div`
  background: ${SAFE_BG};
  border: 1px solid rgba(17, 138, 178, 0.15);
  border-radius: 8px;
  padding: 20px 22px;
  text-align: center;
`;

const SafeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${white};
  color: ${primaryColor};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
`;

const SafeTitle = styled.h4`
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 600;
  color: ${HEADING};
  margin: 0 0 6px;
`;

const SafeText = styled.p`
  font-family: ${fontFamily.font};
  font-size: 12px;
  color: ${BODY};
  margin: 0;
  line-height: 1.5;
`;
